import { Component, computed, DestroyRef, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiUser, AuthService, UsersService } from '@core';
import { UpdateUserDto } from '@core/dto';
import { AuthUser } from '@core/services/auth/auth.interface';
import { formatCooldownTime } from '@features/dealers/components/single-dealer/single-dealer.utils';
import { MainLayoutStore } from '@layouts/main-layout/main-layout.store';
import { Button, Card, Input, Tabs, ToggleButton } from '@ntv360/component-pantry';
import { ConfirmationModalComponent } from '@shared';
import { SVG_ICONS } from '@shared/constants';
import { Logger } from '@shared/utils';
import { map, switchMap } from 'rxjs';

@Component({
    selector: 'app-profile-settings',
    standalone: true,
    imports: [Tabs, ReactiveFormsModule, Card, Input, Button, ConfirmationModalComponent],
    templateUrl: './profile-settings.component.html',
    styleUrl: './profile-settings.component.scss',
})
export class ProfileSettingsComponent implements OnInit, OnDestroy {
    /** Auth service for current user traits retrieval */
    private readonly authService = inject(AuthService);
    /** Destroy ref for RxJS teardown */
    private readonly destroyRef = inject(DestroyRef);
    /** Form builder for profile settings reactive form */
    private readonly fb = inject(FormBuilder);
    /** Users service for profile update requests */
    private readonly usersService = inject(UsersService);
    /** Profile form fields that toggle between read-only and edit mode */
    private readonly profileInfoFields = ['firstName', 'lastName', 'contactNumber'] as const;

    /** Tabs displayed in profile settings page */
    protected readonly tabs = [{ label: 'Profile' }, { label: 'Activity' }];
    /** Active tab index */
    protected readonly activeTab = signal(0);
    /** Whether profile info fields are editable */
    protected readonly isEditProfileEnabled = signal(false);
    /** Whether profile update request is in-flight */
    protected readonly isSavingProfile = signal(false);
    /** Whether user already attempted saving profile with invalid fields */
    protected readonly hasAttemptedProfileSave = signal(false);
    /** Whether change password fields are visible */
    protected readonly showChangePasswordFields = signal(false);
    /** Whether change password confirmation modal is visible */
    protected readonly showChangePasswordModal = signal(false);
    /** Whether change password request is in-flight */
    protected readonly isChangePasswordLoading = signal(false);
    /** Message displayed inside change password confirmation modal */
    protected readonly changePasswordMessage = signal('');
    /** Seconds remaining before change password can be requested again */
    protected readonly changePasswordSecondsLeft = signal(0);
    /** Whether initial cooldown check is still pending */
    protected readonly isCheckingChangePasswordCooldown = signal(true);
    /** Whether change password is currently within cooldown window */
    protected readonly isChangePasswordCoolingDown = computed(
        () => this.changePasswordSecondsLeft() > 0,
    );
    /** Disabled state for change password while loading or cooling down */
    protected readonly changePasswordDisabled = computed(
        () =>
            this.isChangePasswordLoading() ||
            this.isChangePasswordCoolingDown() ||
            this.isCheckingChangePasswordCooldown(),
    );
    /**
     * Human-readable change-password cooldown for the label beside the button
     * (e.g. `3 mins 56 secs`, `56 secs`).
     */
    protected readonly changePasswordCooldownDisplay = computed(() =>
        formatCooldownTime(this.changePasswordSecondsLeft()),
    );
    /** Tooltip text for change password button when cooling down */
    protected readonly changePasswordTooltip = computed(() => {
        if (this.isCheckingChangePasswordCooldown()) return 'Checking…';
        const seconds = this.changePasswordSecondsLeft();
        if (seconds <= 0) return '';
        return `Please wait ${formatCooldownTime(seconds)} before requesting another password reset.`;
    });
    /** Active cooldown interval handle */
    private changePasswordInterval: ReturnType<typeof setInterval> | null = null;
    /** Whether profile save success modal is visible */
    protected readonly showSaveProfileModal = signal(false);
    /** Whether unsaved-changes warning modal is visible */
    protected readonly showCancelEditConfirmModal = signal(false);
    /** Maximum character length for user type, email, first name, last name inputs */
    protected readonly profileFieldMaxLength = 30;
    /** Maximum character length for contact number input */
    protected readonly contactNumberMaxLength = 10;
    /** Current character lengths for length-limited form fields */
    protected readonly fieldLengths = signal<{
        userType: number;
        emailAddress: number;
        firstName: number;
        lastName: number;
        contactNumber: number;
    }>({ userType: 0, emailAddress: 0, firstName: 0, lastName: 0, contactNumber: 0 });
    /** Current authenticated user data used to prefill fields */
    protected readonly currentUser = this.authService.getUserTraits<AuthUser>()?.data ?? null;
    /** Snapshot of last saved profile fields used when canceling edit mode */
    private profileDefaults = {
        userType: this.currentUser?.roleCode ?? '',
        emailAddress: this.currentUser?.emailAddress ?? '',
        firstName: this.currentUser?.firstName ?? '',
        lastName: this.currentUser?.lastName ?? '',
        contactNumber: this.currentUser?.contactNumber ?? '',
    };
    /** Icon registry for template svg references */
    protected readonly icons = SVG_ICONS;
    /** Sprite file path for icon usage */
    protected readonly spritePath = 'assets/icons/icons-sprite.svg';

    /** Reactive profile settings form */
    protected readonly profileForm = this.fb.group({
        userType: [this.profileDefaults.userType, [Validators.required]],
        emailAddress: [this.profileDefaults.emailAddress, [Validators.required, Validators.email]],
        firstName: [this.profileDefaults.firstName, [Validators.required]],
        lastName: [this.profileDefaults.lastName, [Validators.required]],
        contactNumber: [
            this.profileDefaults.contactNumber,
            [Validators.required, Validators.pattern(/^\d{10}$/)],
        ],
        currentPassword: [''],
        newPassword: [''],
        passwordConfirmation: [''],
    });

    /**
     * @public
     * @description Initializes breadcrumb and profile fields readonly state
     * @returns void
     */
    public ngOnInit(): void {
        MainLayoutStore.setBreadcrumb('Profile Settings');
        this.setProfileFieldsEditable(false);
        this.profileForm.get('userType')?.disable({ emitEvent: false });
        this.profileForm.get('emailAddress')?.disable({ emitEvent: false });
        this.refreshChangePasswordCooldown();
        this.loadProfileFromApi();
        this.updateFieldLengths();
        this.profileForm.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.updateFieldLengths());
    }

    /**
     * @private
     * @description Recomputes character counts for length-limited fields from raw form values
     * @returns void
     */
    private updateFieldLengths(): void {
        const v = this.profileForm.getRawValue();
        this.fieldLengths.set({
            userType: (v.userType ?? '').length,
            emailAddress: (v.emailAddress ?? '').length,
            firstName: (v.firstName ?? '').length,
            lastName: (v.lastName ?? '').length,
            contactNumber: (v.contactNumber ?? '').length,
        });
    }

    /**
     * @private
     * @description Fetches latest user record by id and patches form + defaults
     * @returns void
     */
    private loadProfileFromApi(): void {
        const userId = this.currentUser?.userId;
        if (!userId) return;

        this.usersService
            .getUserById(userId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    const apiUser = res?.data;
                    if (!apiUser) return;
                    this.applyProfileData(apiUser);
                },
                error: (err: unknown) => {
                    Logger.error('ProfileSettingsComponent', 'Failed to load profile by id:', err);
                },
            });
    }

    /**
     * @private
     * @description Patches form values and snapshots defaults from given user record
     * @param apiUser - Server-issued user record
     * @returns void
     */
    private applyProfileData(apiUser: ApiUser): void {
        const assignment = apiUser.userClientAssignments?.[0];
        const userType =
            assignment?.clientTypeName ?? assignment?.roleName ?? this.profileDefaults.userType;
        const next = {
            userType,
            emailAddress: apiUser.emailAddress ?? '',
            firstName: apiUser.firstName ?? '',
            lastName: apiUser.lastName ?? '',
            contactNumber: apiUser.contactNumber ?? '',
        };
        this.profileDefaults = next;
        this.profileForm.patchValue(next, { emitEvent: false });
        this.updateFieldLengths();
    }

    /**
     * @public
     * @description Clears breadcrumb on component destroy
     * @returns void
     */
    public ngOnDestroy(): void {
        MainLayoutStore.clearBreadcrumb();
        this.clearChangePasswordTimer();
    }

    /**
     * @protected
     * @description Updates the active tab index
     * @param index - Selected tab index
     * @returns void
     */
    protected onTabChange(index: number): void {
        this.activeTab.set(index);
    }

    /**
     * @protected
     * @description Requests Auth0 password reset email and shows confirmation modal
     * @returns void
     */
    protected onClickChangePassword(): void {
        if (this.changePasswordDisabled()) return;

        const targetEmail =
            this.currentUser?.emailAddress ||
            this.profileForm.get('emailAddress')?.value ||
            'your registered email';

        this.isChangePasswordLoading.set(true);
        this.authService
            .changePassword<{ data?: { message?: string } }>()
            .pipe(
                switchMap(() =>
                    this.authService.getChangePasswordExpiry<{ data: { ttl: number } }>(),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (expiry) => {
                    this.isChangePasswordLoading.set(false);
                    this.changePasswordMessage.set(
                        `We sent an email to ${targetEmail} to reset your password. Please check your inbox to continue.`,
                    );
                    this.showChangePasswordModal.set(true);
                    const ttl = expiry?.data?.ttl ?? 0;
                    if (ttl > 0) this.startChangePasswordTimer(ttl);
                },
                error: (err: unknown) => {
                    this.isChangePasswordLoading.set(false);
                    Logger.error(
                        'ProfileSettingsComponent',
                        'Failed to initiate change password:',
                        err,
                    );
                },
            });
    }

    /**
     * @private
     * @description Fetches current cooldown ttl on mount and starts timer if active
     * @returns void
     */
    private refreshChangePasswordCooldown(): void {
        this.isCheckingChangePasswordCooldown.set(true);
        this.authService
            .getChangePasswordExpiry<{ data: { ttl: number } }>()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    const ttl = res?.data?.ttl ?? 0;
                    if (ttl > 0) this.startChangePasswordTimer(ttl);
                    this.isCheckingChangePasswordCooldown.set(false);
                },
                error: () => {
                    this.isCheckingChangePasswordCooldown.set(false);
                },
            });
    }

    /**
     * @private
     * @description Starts countdown ticking down `changePasswordSecondsLeft` each second
     * @param initialSeconds - Initial cooldown duration in seconds
     * @returns void
     */
    private startChangePasswordTimer(initialSeconds: number): void {
        this.clearChangePasswordTimer();
        this.changePasswordSecondsLeft.set(initialSeconds);
        this.changePasswordInterval = setInterval(() => {
            const next = this.changePasswordSecondsLeft() - 1;
            if (next <= 0) {
                this.changePasswordSecondsLeft.set(0);
                this.clearChangePasswordTimer();
                return;
            }
            this.changePasswordSecondsLeft.set(next);
        }, 1000);
    }

    /**
     * @private
     * @description Cancels active cooldown interval if running
     * @returns void
     */
    private clearChangePasswordTimer(): void {
        if (this.changePasswordInterval !== null) {
            clearInterval(this.changePasswordInterval);
            this.changePasswordInterval = null;
        }
    }

    /**
     * @protected
     * @description Closes the change password confirmation modal
     * @returns void
     */
    protected onCloseChangePasswordModal(): void {
        this.showChangePasswordModal.set(false);
    }

    /**
     * @protected
     * @description Enables editing mode for profile information fields
     * @returns void
     */
    protected onClickEditProfile(): void {
        this.hasAttemptedProfileSave.set(false);
        this.isEditProfileEnabled.set(true);
        this.setProfileFieldsEditable(true);
    }

    /**
     * @protected
     * @description Handles Cancel click. Prompts a warning when form has unsaved changes.
     * @returns void
     */
    protected onClickCancelEditProfile(): void {
        if (this.hasUnsavedProfileChanges()) {
            this.showCancelEditConfirmModal.set(true);
            return;
        }
        this.discardProfileEdits();
    }

    /**
     * @protected
     * @description Confirms discard of unsaved changes from the warning modal
     * @returns void
     */
    protected onConfirmDiscardChanges(): void {
        this.showCancelEditConfirmModal.set(false);
        this.discardProfileEdits();
    }

    /**
     * @protected
     * @description Dismisses the unsaved-changes warning modal and stays in edit mode
     * @returns void
     */
    protected onDismissDiscardChanges(): void {
        this.showCancelEditConfirmModal.set(false);
    }

    /**
     * @private
     * @description Checks whether any tracked profile field differs from last saved snapshot
     * @returns boolean
     */
    private hasUnsavedProfileChanges(): boolean {
        const current = this.profileForm.getRawValue();
        return this.profileInfoFields.some((fieldName) => {
            const currentValue = (current[fieldName] ?? '').trim();
            const defaultValue = (this.profileDefaults[fieldName] ?? '').trim();
            return currentValue !== defaultValue;
        });
    }

    /**
     * @private
     * @description Restores latest saved values and exits edit mode
     * @returns void
     */
    private discardProfileEdits(): void {
        this.profileForm.patchValue(this.profileDefaults);
        this.profileInfoFields.forEach((fieldName) => {
            const control = this.profileForm.get(fieldName);
            control?.markAsPristine();
            control?.markAsUntouched();
        });

        this.hasAttemptedProfileSave.set(false);
        this.isEditProfileEnabled.set(false);
        this.setProfileFieldsEditable(false);
    }

    /**
     * @protected
     * @description Saves profile information when profile controls are valid
     * @returns void
     */
    protected onSaveProfile(): void {
        const userId = this.currentUser?.userId;
        if (!userId || this.isSavingProfile()) return;

        this.hasAttemptedProfileSave.set(true);
        this.markProfileFieldsAsTouched();
        if (!this.areProfileFieldsValid()) return;

        const profileData = this.profileForm.getRawValue();
        const payload: UpdateUserDto = {
            emailAddress: profileData.emailAddress?.trim(),
            firstName: profileData.firstName?.trim(),
            lastName: profileData.lastName?.trim(),
            contactNumber: profileData.contactNumber?.trim(),
        };

        this.isSavingProfile.set(true);
        this.usersService
            .update(userId, payload)
            .pipe(
                map((response) => this.resolveUpdatedUser(response, payload)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (apiUser) => {
                    this.applyProfileData(apiUser);
                    this.syncAuthTraits(apiUser);
                    this.isEditProfileEnabled.set(false);
                    this.setProfileFieldsEditable(false);
                    this.hasAttemptedProfileSave.set(false);
                    this.isSavingProfile.set(false);
                    this.showSaveProfileModal.set(true);
                },
                error: (err: unknown) => {
                    this.isSavingProfile.set(false);
                    Logger.error('ProfileSettingsComponent', 'Failed to update profile:', err);
                },
            });
    }

    /**
     * @private
     * @description Normalizes PATCH response into a user record for local state sync
     * @param response - PATCH body (bare user or `{ data }` wrapper)
     * @param payload - Fields sent in the update request
     * @returns ApiUser with saved profile fields applied
     */
    private resolveUpdatedUser(
        response: ApiUser | { data?: ApiUser },
        payload: UpdateUserDto,
    ): ApiUser {
        const fromApi =
            response && typeof response === 'object' && 'data' in response
                ? response.data
                : (response as ApiUser);

        return {
            userId: fromApi?.userId ?? this.currentUser?.userId ?? '',
            username: fromApi?.username ?? this.currentUser?.userName ?? '',
            emailAddress: fromApi?.emailAddress ?? payload.emailAddress ?? '',
            firstName: fromApi?.firstName ?? payload.firstName ?? '',
            lastName: fromApi?.lastName ?? payload.lastName ?? '',
            contactNumber: fromApi?.contactNumber ?? payload.contactNumber,
            auth0UserId: fromApi?.auth0UserId ?? '',
            statusId: fromApi?.statusId ?? 0,
            userClientAssignments: fromApi?.userClientAssignments,
            organizationIdSignin: fromApi?.organizationIdSignin ?? '',
        };
    }

    /**
     * @protected
     * @description Closes the profile save success modal
     * @returns void
     */
    protected onCloseSaveProfileModal(): void {
        this.showSaveProfileModal.set(false);
    }

    /**
     * @private
     * @description Merges fresh user fields into stored auth traits so navbar reflects update
     * @param apiUser - Server-issued user record
     * @returns void
     */
    private syncAuthTraits(apiUser: ApiUser): void {
        const existing = this.authService.getUserTraits<AuthUser>();
        if (!existing?.data) return;
        const merged: AuthUser = {
            ...existing,
            data: {
                ...existing.data,
                emailAddress: apiUser.emailAddress ?? existing.data.emailAddress,
                firstName: apiUser.firstName ?? existing.data.firstName,
                lastName: apiUser.lastName ?? existing.data.lastName,
                contactNumber: apiUser.contactNumber ?? existing.data.contactNumber,
            },
        };
        this.authService.setAuthenticated(merged);
    }

    /**
     * @protected
     * @description Indicates whether profile save can be submitted
     * @returns boolean
     */
    protected canSaveProfile(): boolean {
        return this.isEditProfileEnabled() && !this.isSavingProfile();
    }

    /**
     * @protected
     * @description Returns validation message for a profile field
     * @param fieldName - Profile form control name
     * @returns string
     */
    protected getProfileFieldErrorMessage(
        fieldName: (typeof this.profileInfoFields)[number],
    ): string {
        const control = this.profileForm.get(fieldName);
        if (!control || !control.errors || !this.isProfileFieldInvalid(fieldName)) {
            return '';
        }

        if (control.errors['required']) {
            return 'This field is required.';
        }

        if (fieldName === 'contactNumber' && control.errors['pattern']) {
            return 'Contact number must be exactly 10 digits.';
        }

        return '';
    }

    /**
     * @protected
     * @description Checks whether a profile field should display invalid state
     * @param fieldName - Profile form control name
     * @returns boolean
     */
    protected isProfileFieldInvalid(fieldName: (typeof this.profileInfoFields)[number]): boolean {
        const control = this.profileForm.get(fieldName);
        if (!control || !this.isEditProfileEnabled()) {
            return false;
        }

        return (
            control.invalid && (control.touched || control.dirty || this.hasAttemptedProfileSave())
        );
    }

    /**
     * @protected
     * @description Sanitizes contact number on each input event to keep only 10 digits
     * @param event - Input event emitted from contact number field
     * @returns void
     */
    protected onContactNumberInput(event: Event): void {
        const inputElement = this.resolveInputElement(event);
        if (!inputElement) return;

        const sanitizedValue = this.normalizeContactNumber(inputElement.value);
        if (inputElement.value !== sanitizedValue) {
            inputElement.value = sanitizedValue;
        }

        const contactNumberControl = this.profileForm.get('contactNumber');
        if (contactNumberControl?.value !== sanitizedValue) {
            contactNumberControl?.setValue(sanitizedValue);
        }
    }

    /**
     * @protected
     * @description Prevents non-digit entry and blocks typing beyond 10 digits
     * @param event - Keyboard event from contact number input
     * @returns void
     */
    protected onContactNumberKeydown(event: KeyboardEvent): void {
        const inputElement = this.resolveInputElement(event);
        if (!inputElement) return;

        const allowedControlKeys = new Set([
            'Backspace',
            'Delete',
            'Tab',
            'Escape',
            'Enter',
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'Home',
            'End',
        ]);

        if (allowedControlKeys.has(event.key) || event.ctrlKey || event.metaKey) {
            return;
        }

        if (!/^\d$/.test(event.key)) {
            event.preventDefault();
            return;
        }

        const selectionStart = inputElement.selectionStart ?? inputElement.value.length;
        const selectionEnd = inputElement.selectionEnd ?? inputElement.value.length;
        const selectedLength = Math.max(selectionEnd - selectionStart, 0);
        const nextLength = inputElement.value.length - selectedLength + 1;

        if (nextLength > 10) {
            event.preventDefault();
        }
    }

    /**
     * @protected
     * @description Cancels password change flow and resets password inputs
     * @returns void
     */
    protected onCancelChangePassword(): void {
        this.profileForm.patchValue({
            currentPassword: '',
            newPassword: '',
            passwordConfirmation: '',
        });
        this.showChangePasswordFields.set(false);
    }

    /**
     * @protected
     * @description Saves new password when required values are present and matched
     * @returns void
     */
    protected onSaveNewPassword(): void {
        const { currentPassword, newPassword, passwordConfirmation } =
            this.profileForm.getRawValue();
        const hasRequiredValues = !!currentPassword && !!newPassword && !!passwordConfirmation;
        const passwordsMatch = newPassword === passwordConfirmation;

        if (!hasRequiredValues || !passwordsMatch) {
            return;
        }

        this.showChangePasswordFields.set(false);
    }

    /**
     * @protected
     * @description Checks whether new password action can be submitted
     * @returns boolean
     */
    protected canSaveNewPassword(): boolean {
        const { currentPassword, newPassword, passwordConfirmation } =
            this.profileForm.getRawValue();
        return !!currentPassword && !!newPassword && !!passwordConfirmation;
    }

    /**
     * @private
     * @description Enables or disables profile info controls based on edit mode
     * @param isEditable - Whether profile controls should be editable
     * @returns void
     */
    private setProfileFieldsEditable(isEditable: boolean): void {
        this.profileInfoFields.forEach((fieldName) => {
            const formControl = this.profileForm.get(fieldName);

            if (!formControl) return;

            if (isEditable) {
                formControl.enable({ emitEvent: false });
                return;
            }

            formControl.disable({ emitEvent: false });
        });
    }

    /**
     * @private
     * @description Marks profile information controls as touched
     * @returns void
     */
    private markProfileFieldsAsTouched(): void {
        this.profileInfoFields.forEach((fieldName) => {
            const control = this.profileForm.get(fieldName);
            control?.markAsTouched();
        });
    }

    /**
     * @private
     * @description Validates profile information controls
     * @returns boolean
     */
    private areProfileFieldsValid(): boolean {
        return this.profileInfoFields.every((fieldName) => {
            const control = this.profileForm.get(fieldName) as AbstractControl | null;
            return !!control && control.valid;
        });
    }

    /**
     * @private
     * @description Normalizes contact number to numeric characters capped at 10 digits
     * @param value - Raw input value from form control
     * @returns string
     */
    private normalizeContactNumber(value: unknown): string {
        return String(value ?? '')
            .replace(/\D/g, '')
            .slice(0, 10);
    }

    /**
     * @private
     * @description Resolves underlying HTML input element from custom input events
     * @param event - DOM event fired by ntv-input or child input
     * @returns HTMLInputElement | null
     */
    private resolveInputElement(event: Event): HTMLInputElement | null {
        const eventTarget = event.target;
        if (eventTarget instanceof HTMLInputElement) {
            return eventTarget;
        }

        if ('composedPath' in event) {
            const inputFromPath = event
                .composedPath()
                .find((node) => node instanceof HTMLInputElement) as HTMLInputElement | undefined;
            return inputFromPath ?? null;
        }

        return null;
    }
}
